// /civil-rate-library-nz — SEO landing page for the Fieldlink Rate Library.
import { Link } from "react-router-dom";
import { ArrowRight, Library, Info } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "../FAQ";
import { MarketingHeader, Breadcrumb, breadcrumbJsonLd } from "./SeoProductPage";

const CATS = [
  ["Drainage", "Pipe laying, bedding, manholes, connections — per metre and per unit rates.", "/tools/pipe-grade-calculator", "Pipe Grade Calculator"],
  ["Earthworks", "Bulk cut, strip, load & cart, fill and compaction — per m³ rates.", "/tools/cut-fill-calculator", "Cut & Fill Calculator"],
  ["Roading", "Basecourse, asphalt, kerbing and reinstatement — per m²/tonne rates.", "/tools/asphalt-tonnage-calculator", "Asphalt Tonnage Calculator"],
  ["Concrete", "Foundations, slabs, footpaths and structures — per m³/m² rates.", "/tools/concrete-calculator", "Concrete Calculator"],
  ["Retaining", "Timber, block and gabion walls — per m² face rates.", "/contractors/retaining", "Find retaining contractors"],
  ["Plant", "Excavators, trucks, rollers — dry and wet hourly charge-out rates.", "/tools/excavator-charge-out-rate-calculator-nz", "Excavator Charge-Out Calculator"],
  ["Labour", "Operators, drainlayers, labourers — fully-costed charge-out rates.", "/tools/labour-charge-out-rate-calculator-nz", "Labour Charge-Out Calculator"],
  ["Materials", "Aggregates, pipe, concrete, asphalt — supplier pricing by quantity.", "/tools/aggregate-calculator", "Aggregate Calculator"],
  ["Cartage", "Truck rates by hour or load, cycle-time based productivity.", "/tools/truck-cycle-calculator", "Truck Cycle Calculator"],
  ["Subcontractors", "Specialist sub rates with markup handled consistently.", "/tools/markup-margin-calculator", "Markup vs Margin Calculator"],
];

export default function RateLibraryNZ() {
  const trail = [{ label: "Home", to: "/" }, { label: "Civil Hub", to: "/resources" }, { label: "Civil Rate Library NZ" }];
  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO
        title="Civil Construction Rate Library NZ | Build Your Own Rates | Fieldlink"
        description="Build and manage your own civil construction rates in NZ — labour, plant, materials, cartage and subcontractor rate build-ups, with free calculators for each category. Indicative guidance only, priced from your actual costs."
        path="/civil-rate-library-nz"
        jsonLd={breadcrumbJsonLd(trail)}
      />
      <MarketingHeader />
      <Breadcrumb trail={trail} />
      <section className="bg-[#f6f5f1] text-[#142635] py-14 border-b border-[#cbd3d5]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">Rates that win work — and make money</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl leading-[0.98]">Civil Rate Library<br /><span className="text-[#a63c0b]">New Zealand</span></h1>
          <p className="text-[#465562] max-w-2xl mt-4">
            The most reliable rate is one built from <b className="text-[#142635]">your</b> costs — your machines, your people, your suppliers, your overheads.
            Fieldlink's Rate Library lets civil contractors build, reuse and manage labour, plant, material, cartage and subcontractor rates so every estimator prices the same way.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/register" className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider text-sm px-6 py-3 inline-flex items-center gap-2" data-testid="rl-cta-build">Build my rate library <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/civil-estimating-software-nz" className="rounded-md border border-[#cbd3d5] bg-white hover:bg-white text-[#142635] font-bold uppercase tracking-wider text-sm px-6 py-3">See civil estimating</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-2">Rate categories</h2>
        <p className="text-[#52616c] text-sm max-w-2xl mb-7">Every category pairs with a free Civil Hub calculator so you can sanity-check the build-up before it goes in a tender.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rl-categories">
          {CATS.map(([t, d, to, label]) => (
            <div key={t} className="rounded-lg border border-[#cbd3d5] bg-white p-5 hover:border-[#FF5F15]/40 transition-colors" data-testid={`rl-cat-${t.toLowerCase()}`}>
              <span className="w-9 h-9 rounded-md bg-[#FF5F15]/12 border border-[#FF5F15]/40 flex items-center justify-center"><Library className="w-4 h-4 text-[#a63c0b]" /></span>
              <h3 className="mt-3 font-display font-black uppercase tracking-tight text-base">{t}</h3>
              <p className="text-[13px] text-[#52616c] mt-1 leading-relaxed">{d}</p>
              <Link to={to} className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#a63c0b] hover:text-[#ff7a3d]">{label} <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#a63c0b] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display font-black uppercase tracking-tight text-xl">Why we don't publish "the going rate"</h2>
              <p className="text-[#465562] text-sm mt-2 leading-relaxed">
                Any indicative civil rate is exactly that — indicative. Real pricing moves with location, quantity, access, ground conditions,
                specification, material supply, plant availability, traffic management, disposal fees and project conditions. A rate that wins in
                South Auckland can lose money in Queenstown. That's why Fieldlink is built around <b className="text-[#142635]">your company's own rates</b>,
                with cost build-ups you control — never a generic number someone told you to charge.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-5">How a rate build-up works</h2>
        <div className="grid md:grid-cols-4 gap-3">
          {[["1. Cost it", "Machine, wages, fuel, materials — the true direct cost per hour, metre or m³."],
            ["2. Add overhead", "Yard, admin, insurance, vehicles — recovered across billable hours."],
            ["3. Add margin", "Margin on sell, not markup on cost — they're not the same thing."],
            ["4. Reuse it", "Save the rate so every quote and estimate prices consistently."]].map(([t, d]) => (
            <div key={t} className="rounded-lg border border-[#cbd3d5] bg-white p-5">
              <div className="font-display font-black uppercase tracking-tight text-base text-[#a63c0b]">{t}</div>
              <p className="text-[13px] text-[#52616c] mt-1.5 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-lg bg-white border border-[#cbd3d5] p-7 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-display font-black uppercase tracking-tight text-xl">Like this rate? Keep it.</div>
            <p className="text-sm text-[#52616c] mt-1">Save and manage your actual company rates in Fieldlink — then price takeoffs, quotes and variations from them.</p>
          </div>
          <Link to="/register" className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider text-sm px-6 py-3 inline-flex items-center gap-2" data-testid="rl-cta-bottom">Build my rate library <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
